import React from 'react';
import { EntityPickerSettings, PickerFolders } from '@decisions/server/DecisionsFramework/Design/Properties/Attributes';
import { IEntityPickerFetcher, EpickTreeConstants } from './entityPickerFetcher';
import { IEpickNode } from './EpickTree';
import { ITreeNode, ITreeNodeAction, TreeNodeActionVisibility } from 'components/Tree/CommonTree/CommonTreeNode';
import { generateGuid } from 'utils/Guid';
import { EntityPickerItem } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/EntityPickerItem';
import { FavoriteType } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Folder/FavoriteType';
import { getImageInfoUrl } from 'utils/ImageUtils';
import { FavoriteIcon } from 'components/FavoriteIcon/FavoriteIcon';
import { setEntityFavorite } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/FavoriteService';
import { getActionsForAnyEntity } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Folder/FolderService';
import { EntityActionType } from '@decisions/server/DecisionsFramework/ServiceLayer/Actions/EntityActionType';

export interface IEntityPickerTreeModel {
  getRootNodes(): Promise<ITreeNode<IEpickNode>[]>;
  getSubNodes(nodeId: string): Promise<ITreeNode<IEpickNode>[]>;
  getFavoritesRootNode?: () => Promise<ITreeNode<IEpickNode>[]>;
  getRecentRootNode?: () => Promise<ITreeNode<IEpickNode>[]>;
  getRootFolderNode?: () => Promise<ITreeNode<IEpickNode>[]>;
  getCurrentFolderNode?: () => Promise<ITreeNode<IEpickNode>[]>;
  getSubNodesForFolder?: (nodeId: string) => Promise<ITreeNode<IEpickNode>[]>;
  getAllRootNode?: () => Promise<ITreeNode<IEpickNode>[]>;
  sortNodes?: (a: ITreeNode<IEpickNode>, b: ITreeNode<IEpickNode>) => number;
  getRootFolders?: () => Promise<ITreeNode<IEpickNode>[]>;
  getRecentNodes?: () => Promise<ITreeNode<IEpickNode>[]>;
  getFavoriteNodes?: () => Promise<ITreeNode<IEpickNode>[]>;
  search?: string;
}

const imageSize = 20;
export function getTreeNode(epickNode: IEpickNode): ITreeNode<IEpickNode> {
  return {
    nodeId: epickNode.id + generateGuid(),
    isExpanded: !!epickNode.expanded,
    name: epickNode.name,
    hasChildren: epickNode.expandable,
    isSelectable: epickNode.pickable,
    description: epickNode.title,
    icon: epickNode.entity?.Icon ? (
      <img
        src={getImageInfoUrl(epickNode.entity?.Icon, imageSize, imageSize, '#808080') ?? ''}
        alt={`Entity Icon`}
        style={{ maxWidth: imageSize, height: imageSize }}
      />
    ) : (
      undefined
    ),
    actions: getEntityActions(epickNode),
    data: epickNode,
  };
}

/**
 * DefaultEntityPickerTreeModel
 */
export function defaultEpickTreeModel(
  settings: EntityPickerSettings,
  dataFetcher: IEntityPickerFetcher,
  additionalRootNodes?: IEpickNode[],
  areFoldersPickable: boolean = false
): IEntityPickerTreeModel {
  const entityPickerFetcher: IEntityPickerFetcher = dataFetcher;
  const getRootNodes = async (): Promise<ITreeNode<IEpickNode>[]> => {
    const results = await Promise.all([
      getFavoritesRootNode(),
      getRecentRootNode(),
      getRootFolderNode(),
      getCurrentFolderNode(),
      getAllRootNode(),
    ]);

    const rootNodes = [
      ...results.flatMap(item => item),
      ...(additionalRootNodes?.map(node => getTreeNode(node)) ?? []),
    ];

    return rootNodes;
  };

  const getSubNodes = async (nodeId: string): Promise<ITreeNode<IEpickNode>[]> => {
    switch (nodeId) {
      case EpickTreeConstants.favoriteItemsFolderId: {
        return getFavoriteNodes();
      }
      case EpickTreeConstants.recentItemsFolderId: {
        return getRecentNodes();
      }
      case EpickTreeConstants.allFoldersId: {
        return getRootFolders();
      }
      default: {
        return getSubNodesForFolder(nodeId);
      }
    }
  };

  const getSubNodesForFolder = async (nodeId: string): Promise<ITreeNode<IEpickNode>[]> => {
    const subFolders = await dataFetcher.fetchSubFolders(nodeId);
    const entites = dataFetcher.fetchEntities(nodeId);

    return [
      ...subFolders.map(s =>
        getTreeNode({
          id: s.Id,
          name: s.EntityName,
          favoriteType: s.FavoriteType,
          expandable: areFoldersPickable ? s.HasSubFolders : true,
          pickable: areFoldersPickable,
          canBeFav: areFoldersPickable,
        } as IEpickNode)
      ),
      ...(await entites).map(e =>
        getTreeNode({
          id: e.Id,
          favoriteType: e.FavoriteType,
          name: e.EntityName,
          pickable: true,
          canBeFav: true,
          entity: e,
        } as IEpickNode)
      ),
    ];
  };

  const getRootFolders = async (): Promise<ITreeNode<IEpickNode>[]> => {
    const nodes = await dataFetcher.fetchRootFolders();
    return nodes.map(node =>
      getTreeNode({
        id: node.Id,
        name: node.EntityName,
        favoriteType: node.FavoriteType,
        expandable: areFoldersPickable ? node.HasSubFolders : true,
        pickable: areFoldersPickable,
        canBeFav: areFoldersPickable,
      } as IEpickNode)
    );
  };

  const getRecentNodes = async (): Promise<ITreeNode<IEpickNode>[]> => {
    const nodes = await dataFetcher.fetchRecentEntities();
    return nodes.map(r =>
      getTreeNode({
        id: r.Id,
        name: r.EntityName,
        favoriteType: r.FavoriteType,
        pickable: true,
        canBeFav: true,
        entity: r,
      } as IEpickNode)
    );
  };

  const getFavoriteNodes = async (): Promise<ITreeNode<IEpickNode>[]> => {
    const nodes = await dataFetcher.fetchFavorites();
    if (nodes && nodes.length) {
      return nodes.map(getFavNode);
    }
    return [];
  };

  const getFavNode = (fav: EntityPickerItem) => {
    return getTreeNode({
      id: fav.Id,
      name: fav.EntityName,
      pickable: true,
      isFav: true,
      favoriteType: fav.FavoriteType,
      shouldShowFavoritesMenu: true,
      canBeFav: true,
      entity: fav,
    } as IEpickNode);
  };

  const sortNodes = (a: ITreeNode<IEpickNode>, b: ITreeNode<IEpickNode>): number => {
    if (!a.data || !b.data) {
      return 0;
    }

    if (a.data.order && !b.data.order) {
      return 1;
    }
    if (!a.data.order && b.data.order) {
      return -1;
    }
    if (a.data.order && b.data.order) {
      return a.data.order - b.data.order;
    }

    return a.data.name.localeCompare(b.data.name);
  };

  const showFolder = (pickerFolder: PickerFolders): boolean => {
    return (settings.ShowFolders & pickerFolder) === pickerFolder;
  };

  const expandFolder = (pickerFolder: PickerFolders): boolean => {
    return (settings.ExpandFolders & pickerFolder) === pickerFolder;
  };

  const getFavoritesRootNode = async (): Promise<ITreeNode<IEpickNode>[]> => {
    const nodes = await entityPickerFetcher.fetchFavorites();
    if (nodes?.length) {
      const favoritesNode: IEpickNode = {
        id: EpickTreeConstants.favoriteItemsFolderId,
        name: EpickTreeConstants.favoritesFolderName,
        expanded: expandFolder(PickerFolders.Favorites),
        favoriteType: FavoriteType.None,
        expandable: true,
        order: 1,
        note: nodes.length.toString(),
      };

      const treeNode = getTreeNode(favoritesNode);
      if (treeNode.isExpanded) {
        treeNode.childNodes = nodes.map(node => getFavNode(node));
      }
      return [treeNode] as ITreeNode<IEpickNode>[];
    }
    return [];
  };

  const getRecentRootNode = async (): Promise<ITreeNode<IEpickNode>[]> => {
    const nodes = await entityPickerFetcher.fetchRecentEntities();
    if (nodes.length) {
      const favoritesNode: IEpickNode = {
        id: EpickTreeConstants.recentItemsFolderId,
        name: EpickTreeConstants.recentFolderName,
        expanded: expandFolder(PickerFolders.Recent),
        favoriteType: FavoriteType.None,
        expandable: true,
        order: 2,
        note: nodes.length.toString(),
      };

      return [getTreeNode(favoritesNode)] as ITreeNode<IEpickNode>[];
    }
    return [];
  };

  const getRootFolderNode = async (): Promise<ITreeNode<IEpickNode>[]> => {
    if (showFolder(PickerFolders.Root) && settings.RootFolderId?.length) {
      const folder = await dataFetcher.fetchFolder(settings.RootFolderId);
      if (folder) {
        return [
          getTreeNode({
            id: folder.Id,
            name: folder.EntityName,
            favoriteType: folder.FavoriteType,
            expandable: true,
            pickable: false,
            canBeFav: false,
            expanded: expandFolder(PickerFolders.Root),
            order: 3,
          } as IEpickNode),
        ];
      }
    }
    return [];
  };

  const getCurrentFolderNode = async (): Promise<ITreeNode<IEpickNode>[]> => {
    const currentFolderId = settings.CurrentFolderId ?? Decisions.Navigation.currentFolderId;
    if (showFolder(PickerFolders.Current) && currentFolderId?.length) {
      const folder = await dataFetcher.fetchFolder(currentFolderId);
      if (folder) {
        const entities = await dataFetcher.fetchEntities(currentFolderId);
        const node: IEpickNode = {
          id: folder.Id,
          name: folder.EntityName,
          expanded: expandFolder(PickerFolders.Current),
          expandable: entities && entities.filter(e => !e.Hidden).length > 0,
          favoriteType: FavoriteType.None,
          pickable: areFoldersPickable,
          canBeFav: areFoldersPickable,
          order: 3,
          title: EpickTreeConstants.currentFolderName,
        };

        return [getTreeNode(node)];
      }
    }
    return [];
  };

  const getAllRootNode = async (): Promise<ITreeNode<IEpickNode>[]> => {
    const root = {
      id: EpickTreeConstants.allFoldersId,
      name: EpickTreeConstants.allFoldersName,
      favoriteType: FavoriteType.None,
      expanded: false, //expandFolder(PickerFolders.All),
      expandable: true,
      order: 100,
    } as IEpickNode;
    return [getTreeNode(root)] as ITreeNode<IEpickNode>[];
  };

  return {
    getRootNodes,
    getSubNodes,
    sortNodes,
    getAllRootNode,
    getCurrentFolderNode,
    getFavoritesRootNode,
    getRecentRootNode,
    getRootFolderNode,
    getSubNodesForFolder,
    getRootFolders,
    getRecentNodes,
    getFavoriteNodes,
  };
}

export function getEntityActions(epickNode: IEpickNode): ITreeNodeAction[] {
  if (!epickNode.canBeFav) return [];

  return [
    {
      visibility:
        epickNode.favoriteType === FavoriteType.None
          ? TreeNodeActionVisibility.onHover
          : TreeNodeActionVisibility.always,
      element: (
        <FavoriteIcon
          onChange={async (type: FavoriteType) => {
            setEntityFavorite({ entityId: epickNode.id, favoriteType: type });
          }}
          onEdit={() => {
            runEditFavoriteAction(epickNode.id);
          }}
          classNamePostfix="new"
          currentType={epickNode.favoriteType ?? FavoriteType.None}
          shouldOpenMenu={epickNode.isFav}
        />
      ),
    },
  ] as ITreeNodeAction[];
}

async function runEditFavoriteAction(entityId: string) {
  const actions = await getActionsForAnyEntity({
    actionTypes: [EntityActionType.Mvc],
    entityID: entityId,
    typeName: 'DecisionsFramework.ServiceLayer.Actions.ActionsForEntityPickerHeader',
  });
  if (actions && actions.length === 1) {
    $DP.Common.Actions.ActionExecutor.runActionSync(actions[0], null, null, null);
  } else {
    console.warn('No Favorite action found.');
  }
}
